import React from "react";
import ReactDOM from "react-dom/client";
import { App } from "./App";
// §chrome-liquid (2026-08-10) — the ORIKIN console design system's canonical
// token block, imported ONCE at app root so the tenant console and Mission
// Control read one palette instead of two copies of it. Vocabulary only: the
// light/dark bindings live in themes.css + xpand-design.css. Loaded first so
// the raw --ork-* swatches are in scope for everything that follows.
import "./styles/orikin-tokens.css";
// §3.R2.2: global.css carries the platform + tenant tokens inline
// (see that file for why). tokens.css is a thin stub now.
import "./styles/global.css";
// §v3.kit (2026-05-01) — design-system component classes (sidebar,
// topbar, kpi cards, conv items, bubbles, ctx panel, chips, buttons).
// Imported AFTER global.css so its rules override / inherit from the
// platform tokens defined there.
import "./styles/kit.css";
// §notif-center (2026-06-09) — in-app notification bell + toasts. Reads the
// global :root tokens from xpand-design.css (loaded via global.css above).
import "./styles/notifications.css";
// §v3.kb-redesign (2026-06-13) — the Knowledge operator surface, ported from
// the approved mockup. Fully scoped under .kb-redesign so it cannot leak.
import "./styles/knowledge-redesign.css";
import { applySavedTheme, applyTenantAttr } from "./lib/theme";

// §v3.gradient-themes (2026-05-05) — apply persisted theme BEFORE
// React renders so users don't see a flash of the default palette
// while the saved class lands. Idempotent + safe in SSR contexts.
applySavedTheme();
// §v3.tenant-attr (2026-05-15) — stamp data-tenant on <html> so the
// tenant-scoped CSS blocks (e.g. the roze dashboard wallpaper in
// app-canvas.css) activate — or stay dormant — before first paint.
// Same idempotent contract as applySavedTheme. ProfilePicker calls
// applyTenantAttr() again after a profile switch so the attribute
// updates without reload.
applyTenantAttr();

// §v3.staging-banner — an unmistakable marker when this build is served from
// the STAGING host, so staging can never be mistaken for production. Pure DOM
// so it shows on EVERY screen (login, profile picker, dashboard). Activates
// ONLY on a ``staging.*`` hostname — the identical build on the prod host shows
// nothing. ``pointer-events:none`` everywhere so it never blocks the UI.
if (typeof window !== "undefined" && window.location.hostname.startsWith("staging.")) {
  const markStaging = () => {
    if (!document.body || document.getElementById("__xpand_staging_marker")) return;
    const frame = document.createElement("div");
    frame.id = "__xpand_staging_marker";
    frame.style.cssText =
      "position:fixed;inset:0;z-index:2147483647;pointer-events:none;" +
      "border:3px solid #f59e0b";
    const pill = document.createElement("div");
    pill.textContent = "STAGING · test environment · synthetic data";
    pill.style.cssText =
      "position:absolute;left:14px;bottom:14px;background:#b45309;color:#fff;" +
      "font:600 11px/1.5 system-ui,-apple-system,sans-serif;letter-spacing:.05em;" +
      "padding:5px 12px;border-radius:9999px;box-shadow:0 2px 10px rgba(0,0,0,.55)";
    frame.appendChild(pill);
    document.body.appendChild(frame);
  };
  if (document.body) markStaging();
  else window.addEventListener("DOMContentLoaded", markStaging);
  // Keep the browser tab clearly labelled even as the app updates the title.
  const tagTitle = () => {
    if (!document.title.startsWith("[STAGING]")) {
      document.title = "[STAGING] " + document.title.replace(/^\[STAGING\]\s*/, "");
    }
  };
  tagTitle();
  setInterval(tagTitle, 2000);
}

const rootEl = document.getElementById("root");
if (!rootEl) {
  throw new Error("#root element is missing from index.html");
}

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// §v3.push-sw (2026-05-14) — register the service worker that
// handles push events + notification clicks. Lives at /sw.js
// (web/public/sw.js → web-dist/sw.js on deploy). Registration is
// idempotent: if a SW is already registered at this scope the
// browser returns the existing one. We don't await — nothing in
// the UI depends on the SW being ready immediately; the push
// subscribe flow in Settings handles "SW not ready yet" lazily.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((err) => {
        // SW failure is non-fatal — push UI in Settings will show
        // "Notifications unavailable in this browser."
         
        console.warn("SW registration failed:", err);
      });
  });
}
